import { createContext, useContext, useEffect, useMemo, useReducer, type Dispatch, type ReactNode } from 'react';
import { codeFor, nextCode, type CodeMap } from '../domain/agentCode';
import { cardStatus } from '../domain/agentStatus';
import { mergeLayout, withRunStats, type RunStatsMap } from '../domain/catalog';
import { filterGroups } from '../domain/filtering';
import { UNREGISTERED_SECTION } from '../domain/unregistered';
import { clampProgress, plannedToCard } from '../domain/plannedCard';
import { reorder } from '../domain/reorder';
import { sortCards } from '../domain/sorting';
import { readStored, writeStored, STORAGE_KEYS } from '../domain/storage';
import type {
  AgentCard,
  AgentGroup,
  PlannedAgent,
  RuntimeFilter,
  SectionSeed,
  SortMode,
  StatusFilter,
  ThemeMode,
} from '../types';

/** Fields a user can override from the detail view. */
export type EditableField = 'name' | 'objective' | 'sub' | 'metric' | 'next';

/** Persisted section edits; custom sections are rebuilt from the entries flagged `custom`. */
export interface SectionMeta {
  title?: string;
  desc?: string;
  prefix?: string;
  custom?: boolean;
}

export interface CommandCenterState {
  sections: SectionSeed[];
  /** Cards as served by GET /agent/commandCenter/catalog; empty until the query resolves. */
  catalogCards: AgentCard[];
  /** Server-allocated agent codes, which outrank anything allocated locally. */
  catalogCodes: CodeMap;
  /** False until the catalog has been folded in; nothing is persisted before that. */
  hydrated: boolean;
  /** The saved section order, kept only until hydration applies it to the server's sections. */
  storedSectionOrder: string[];
  /** Manual card placement: section slug → ordered agent ids. */
  layout: Record<string, string[]>;
  owners: Record<string, string>;
  overrides: Record<string, Partial<Record<EditableField, string>>>;
  planned: Record<string, PlannedAgent>;
  codes: Record<string, string>;
  folded: Record<string, boolean>;
  sectionMeta: Record<string, SectionMeta>;
  sectionOwners: Record<string, string>;
  tab: string;
  status: StatusFilter;
  runtime: RuntimeFilter;
  sort: SortMode;
  theme: ThemeMode;
  toast: { id: number; message: string } | null;
}

export type CommandCenterAction =
  | {
      type: 'hydrateCatalog';
      sections: SectionSeed[];
      cards: AgentCard[];
      codes: CodeMap;
      layout: Record<string, string[]>;
    }
  | { type: 'setTab'; tab: string }
  | { type: 'setStatus'; status: StatusFilter }
  | { type: 'setRuntime'; runtime: RuntimeFilter }
  | { type: 'setSort'; sort: SortMode }
  | { type: 'toggleFold'; slug: string }
  | { type: 'foldAll'; folded: boolean }
  | { type: 'setSectionMeta'; slug: string; meta: SectionMeta }
  | { type: 'setSectionOwner'; slug: string; owner: string }
  | { type: 'setOwner'; id: string; owner: string }
  | { type: 'editField'; id: string; field: EditableField; value: string }
  | { type: 'addIdea'; plan: Omit<PlannedAgent, 'id' | 'createdAt' | 'progress'> }
  | { type: 'updateIdea'; id: string; patch: Partial<PlannedAgent> }
  | { type: 'deleteIdea'; id: string }
  | { type: 'moveCard'; id: string; toSection: string; toIndex: number }
  | { type: 'moveSection'; from: number; to: number }
  | { type: 'addSection'; title: string; desc: string; owner: string }
  | { type: 'deleteSection'; slug: string }
  | { type: 'setTheme'; theme: ThemeMode }
  | { type: 'toast'; message: string };

/** Sections the user created locally; the server does not host them yet. */
const customSections = (meta: Record<string, SectionMeta>): SectionSeed[] =>
  Object.entries(meta)
    .filter(([, m]) => m.custom)
    .map(([slug, m]) => ({
      slug,
      title: m.title ?? slug,
      desc: m.desc ?? '',
      owner: 'Kuen',
      prefix: m.prefix ?? 'XX',
    }));

/** Applies the user's saved section order; sections it does not mention keep their server order at the end. */
const rankSections = (sections: SectionSeed[], order: string[]): SectionSeed[] =>
  order.length === 0
    ? sections
    : [...sections].sort((a, b) => {
        const ai = order.indexOf(a.slug);
        const bi = order.indexOf(b.slug);
        return (ai === -1 ? order.length : ai) - (bi === -1 ? order.length : bi);
      });

/** Restores the persisted slices; the board itself stays empty until the catalog query hydrates it. */
export const initialState = (): CommandCenterState => {
  const meta = readStored<Record<string, SectionMeta>>(STORAGE_KEYS.sections, {});
  const order = readStored<string[]>(STORAGE_KEYS.sectionOrder, []);

  return {
    sections: rankSections(customSections(meta), order),
    catalogCards: [],
    catalogCodes: {},
    hydrated: false,
    storedSectionOrder: order,
    layout: readStored<Record<string, string[]>>(STORAGE_KEYS.layout, {}),
    owners: readStored(STORAGE_KEYS.owners, {}),
    overrides: readStored(STORAGE_KEYS.overrides, {}),
    planned: readStored(STORAGE_KEYS.planned, {}),
    codes: readStored(STORAGE_KEYS.codes, {}),
    folded: readStored(STORAGE_KEYS.folded, {}),
    sectionMeta: meta,
    sectionOwners: readStored(STORAGE_KEYS.sectionOwners, {}),
    tab: 'all',
    status: 'all',
    runtime: 'all',
    sort: 'manual',
    theme: 'light',
    toast: null,
  };
};

const withToast = (state: CommandCenterState, message: string): CommandCenterState => ({
  ...state,
  toast: { id: Date.now() + Math.random(), message },
});

const slugify = (name: string): string =>
  name
    .trim()
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || `sec-${Date.now().toString(36)}`;

/** Two-letter prefix derived from the section name, avoiding collisions with existing ones. */
const genPrefix = (name: string, used: Set<string>): string => {
  const letters = name.toUpperCase().match(/[A-Z0-9]/g) ?? ['X', 'X'];
  const first = letters[0] ?? 'X';
  const candidates = letters.slice(1).map((l) => first + l);
  for (let c = 65; c <= 90; c++) {
    candidates.push(first + String.fromCharCode(c));
  }
  return candidates.find((p) => !used.has(p)) ?? `${first}9`;
};

const removeFromLayout = (layout: Record<string, string[]>, id: string): Record<string, string[]> =>
  Object.fromEntries(Object.entries(layout).map(([slug, ids]) => [slug, ids.filter((x) => x !== id)]));

export const reducer = (state: CommandCenterState, action: CommandCenterAction): CommandCenterState => {
  switch (action.type) {
    /**
     * Folds a catalog response into the board. Section order and card placement stay the user's:
     * the server supplies the set, localStorage supplies the arrangement.
     */
    case 'hydrateCatalog': {
      // Before the first hydration the saved order is all there is; a refetch must respect any
      // reordering done since, so from then on the live order wins.
      const order = state.hydrated ? state.sections.map((s) => s.slug) : state.storedSectionOrder;
      return {
        ...state,
        sections: rankSections([...action.sections, ...customSections(state.sectionMeta)], order),
        catalogCards: action.cards,
        catalogCodes: action.codes,
        hydrated: true,
        layout: mergeLayout(action.layout, state.layout, Object.keys(state.planned)),
      };
    }
    case 'setTab':
      return { ...state, tab: action.tab };
    case 'setStatus':
      return { ...state, status: action.status };
    case 'setRuntime':
      return { ...state, runtime: action.runtime };
    case 'setSort':
      return { ...state, sort: action.sort };
    case 'toggleFold':
      return { ...state, folded: { ...state.folded, [action.slug]: !isSectionFolded(state, action.slug) } };
    case 'foldAll':
      return {
        ...state,
        folded: Object.fromEntries(
          [...state.sections.map((s) => s.slug), UNREGISTERED_SECTION].map((slug) => [slug, action.folded]),
        ),
      };
    case 'setSectionMeta':
      return {
        ...state,
        sectionMeta: { ...state.sectionMeta, [action.slug]: { ...state.sectionMeta[action.slug], ...action.meta } },
      };
    case 'setSectionOwner':
      return { ...state, sectionOwners: { ...state.sectionOwners, [action.slug]: action.owner } };
    case 'setOwner': {
      const plan = state.planned[action.id];
      return {
        ...state,
        owners: { ...state.owners, [action.id]: action.owner },
        planned: plan ? { ...state.planned, [action.id]: { ...plan, owner: action.owner } } : state.planned,
      };
    }
    case 'editField': {
      const plan = state.planned[action.id];
      if (plan) {
        const key = action.field === 'sub' ? 'note' : action.field;
        return { ...state, planned: { ...state.planned, [action.id]: { ...plan, [key]: action.value } } };
      }
      return {
        ...state,
        overrides: {
          ...state.overrides,
          [action.id]: { ...state.overrides[action.id], [action.field]: action.value },
        },
      };
    }
    case 'addIdea': {
      const id = `plan-${Date.now().toString(36)}${Math.floor(Math.random() * 1000)}`;
      const plan: PlannedAgent = { ...action.plan, id, progress: 0, createdAt: Date.now() };
      const prefix = state.sections.find((s) => s.slug === plan.section)?.prefix ?? 'XX';
      return withToast(
        {
          ...state,
          planned: { ...state.planned, [id]: plan },
          codes: { ...state.codes, [id]: nextCode(prefix, state.catalogCodes, state.codes) },
          layout: { ...state.layout, [plan.section]: [...(state.layout[plan.section] ?? []), id] },
        },
        `Idea added → ${plan.name}`,
      );
    }
    case 'updateIdea': {
      const plan = state.planned[action.id];
      if (!plan) {
        return state;
      }
      const patched = { ...plan, ...action.patch };
      return {
        ...state,
        planned: { ...state.planned, [action.id]: { ...patched, progress: clampProgress(patched.progress) } },
      };
    }
    case 'deleteIdea': {
      const planned = { ...state.planned };
      delete planned[action.id];
      return withToast({ ...state, planned, layout: removeFromLayout(state.layout, action.id) }, 'Idea deleted');
    }
    case 'moveCard': {
      const cleared = removeFromLayout(state.layout, action.id);
      const target = [...(cleared[action.toSection] ?? [])];
      target.splice(Math.max(0, Math.min(action.toIndex, target.length)), 0, action.id);
      return withToast({ ...state, layout: { ...cleared, [action.toSection]: target } }, 'Layout saved');
    }
    case 'moveSection':
      return withToast({ ...state, sections: reorder(state.sections, action.from, action.to) }, 'Section order saved');
    case 'addSection': {
      const slug = slugify(action.title);
      if (state.sections.some((s) => s.slug === slug)) {
        return withToast(state, 'That section already exists');
      }
      const prefix = genPrefix(action.title, new Set(state.sections.map((s) => s.prefix)));
      const section: SectionSeed = { slug, title: action.title, desc: action.desc, owner: action.owner, prefix };
      return withToast(
        {
          ...state,
          sections: [...state.sections, section],
          layout: { ...state.layout, [slug]: [] },
          sectionMeta: {
            ...state.sectionMeta,
            [slug]: { title: action.title, desc: action.desc, prefix, custom: true },
          },
          sectionOwners: { ...state.sectionOwners, [slug]: action.owner },
        },
        `Section created → ${action.title} (${prefix})`,
      );
    }
    case 'deleteSection': {
      if ((state.layout[action.slug] ?? []).length > 0) {
        return withToast(state, 'Move or delete its cards first');
      }
      const layout = { ...state.layout };
      const sectionMeta = { ...state.sectionMeta };
      delete layout[action.slug];
      delete sectionMeta[action.slug];
      return withToast(
        { ...state, sections: state.sections.filter((s) => s.slug !== action.slug), layout, sectionMeta },
        'Section deleted',
      );
    }
    case 'setTheme':
      return { ...state, theme: action.theme };
    case 'toast':
      return withToast(state, action.message);
    default:
      return state;
  }
};

/** Catalog cards with their run stats folded in, then the user's overrides and owner edits on top. */
const resolveCards = (state: CommandCenterState, stats: RunStatsMap): Map<string, AgentCard> => {
  const map = new Map<string, AgentCard>();
  state.catalogCards.forEach((served) => {
    const base = withRunStats(served, stats[served.id]);
    const override = state.overrides[base.id] ?? {};
    const goal = override.objective ?? base.goal;
    map.set(base.id, {
      ...base,
      name: override.name ?? base.name,
      goal,
      goalParts: override.objective === undefined ? base.goalParts : [{ text: goal, bold: false }],
      sub: override.sub ?? base.sub,
      owner: state.owners[base.id] ?? base.owner,
    });
  });
  Object.values(state.planned).forEach((plan) => {
    const card = plannedToCard(plan);
    map.set(plan.id, { ...card, owner: state.owners[plan.id] ?? card.owner });
  });
  return map;
};

/** Cards grouped per section, after layout, default order, sort and filters. */
export const selectGroups = (state: CommandCenterState, stats: RunStatsMap = {}): AgentGroup[] => {
  const cards = resolveCards(state, stats);
  const groups = state.sections
    .filter((section) => state.tab === 'all' || state.tab === section.slug)
    .map((section) => {
      const ids = state.layout[section.slug] ?? [];
      const placed = ids.map((id) => cards.get(id)).filter((c): c is AgentCard => c !== undefined);
      // 'manual' is the drag order, so it renders the stored layout as-is; re-ranking it here made a
      // drop land somewhere else than where the user let go.
      const ordered = sortCards(placed, state.sort);
      return { slug: section.slug, cards: ordered };
    });
  return filterGroups(groups, state.status, state.runtime);
};

export const selectAllCards = (state: CommandCenterState, stats: RunStatsMap = {}): AgentCard[] =>
  selectGroups(state, stats).flatMap((group) => group.cards);

/** The 미등록 section starts folded so 100+ unregistered skills do not bury the board; a user's choice wins. */
export const isSectionFolded = (state: CommandCenterState, slug: string): boolean =>
  state.folded[slug] ?? slug === UNREGISTERED_SECTION;

export const selectCode = (state: CommandCenterState, id: string): string | null =>
  codeFor(id, state.catalogCodes, state.codes);

export interface TabModel {
  slug: string;
  title: string;
  health: 'healthy' | 'unhealthy' | 'none';
}

/** Tab strip with a health dot per section; sections without agents get no dot. */
export const selectTabs = (state: CommandCenterState, stats: RunStatsMap = {}): TabModel[] => {
  const cards = resolveCards(state, stats);
  return [
    { slug: 'all', title: 'View All', health: 'none' as const },
    ...state.sections.map((section) => {
      const placed = (state.layout[section.slug] ?? [])
        .map((id) => cards.get(id))
        .filter((c): c is AgentCard => c !== undefined);
      const unhealthy = placed.some((c) => cardStatus(c) === 'warn' || cardStatus(c) === 'crit');
      return {
        slug: section.slug,
        title: state.sectionMeta[section.slug]?.title ?? section.title,
        health: (placed.length === 0 ? 'none' : unhealthy ? 'unhealthy' : 'healthy') as TabModel['health'],
      };
    }),
  ];
};

interface Store {
  state: CommandCenterState;
  dispatch: Dispatch<CommandCenterAction>;
}

const CommandCenterContext = createContext<Store | null>(null);

export const CommandCenterProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, undefined, initialState);

  // Persisting before hydration would write the empty pre-catalog board over the user's saved arrangement.
  useEffect(() => {
    if (!state.hydrated) {
      return;
    }
    writeStored(STORAGE_KEYS.layout, state.layout);
    writeStored(STORAGE_KEYS.owners, state.owners);
    writeStored(STORAGE_KEYS.overrides, state.overrides);
    writeStored(STORAGE_KEYS.planned, state.planned);
    writeStored(STORAGE_KEYS.codes, state.codes);
    writeStored(STORAGE_KEYS.folded, state.folded);
    writeStored(STORAGE_KEYS.sections, state.sectionMeta);
    writeStored(STORAGE_KEYS.sectionOwners, state.sectionOwners);
    writeStored(
      STORAGE_KEYS.sectionOrder,
      state.sections.map((s) => s.slug),
    );
  }, [
    state.hydrated,
    state.layout,
    state.owners,
    state.overrides,
    state.planned,
    state.codes,
    state.folded,
    state.sectionMeta,
    state.sectionOwners,
    state.sections,
  ]);

  const value = useMemo(() => ({ state, dispatch }), [state]);
  return <CommandCenterContext.Provider value={value}>{children}</CommandCenterContext.Provider>;
};

export const useCommandCenter = (): Store => {
  const store = useContext(CommandCenterContext);
  if (!store) {
    throw new Error('useCommandCenter must be used inside CommandCenterProvider');
  }
  return store;
};
